export function applyTextEvent(buffer, event) {
    const text = (event?.content?.parts ?? [])
        .map((part) => part.text)
        .filter(Boolean)
        .join("");

    if (!text) {
        return buffer;
    }

    return event.partial === true ? `${buffer}${text}` : text;
}

export function normalizeSessions(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }
    if (Array.isArray(payload?.sessions)) {
        return payload.sessions;
    }
    if (Array.isArray(payload?.output?.sessions)) {
        return payload.output.sessions;
    }
    return [];
}

function sessionRecord(session) {
    if (!session || typeof session !== "object") {
        return session;
    }
    if (session.output && typeof session.output === "object" && (session.output.id || session.output.events || session.output.session_id)) {
        return session.output;
    }
    if (session.session && typeof session.session === "object" && (session.session.id || session.session.events)) {
        return session.session;
    }
    return session;
}

export function sessionIdOf(session) {
    const record = sessionRecord(session);
    return record?.id || record?.sessionId || record?.session_id || "";
}

export function firstUserMessageText(session) {
    const events = sessionEvents(session);
    for (let i = 0; i < events.length; i += 1) {
        const event = events[i];
        if (isUserAuthor(event)) {
            const text = textFromParts(event).trim();
            if (text) {
                return text;
            }
        }
    }
    return "";
}

export function sessionUpdatedAt(session) {
    const record = sessionRecord(session);
    return parseTimestamp(
        record?.last_update_time ??
            record?.lastUpdateTime ??
            record?.update_time ??
            record?.updateTime ??
            record?.create_time ??
            record?.createTime
    );
}

export function sortSessionsByUpdatedAt(sessions) {
    return [...sessions].sort((a, b) => {
        const bTime = sessionUpdatedAt(b)?.getTime() ?? 0;
        const aTime = sessionUpdatedAt(a)?.getTime() ?? 0;
        return bTime - aTime;
    });
}

export function sessionTitle(session, titleMap, t, language) {
    const fromEvents = firstUserMessageText(session);
    if (fromEvents) {
        return truncate(fromEvents, 48);
    }

    const id = sessionIdOf(session);
    if (id && titleMap?.[id]) {
        return titleMap[id];
    }

    const stateTitle =
        sessionRecord(session)?.state?.title ||
        sessionRecord(session)?.state?.chat_title ||
        sessionRecord(session)?.state?.chatTitle;
    if (stateTitle) {
        return truncate(String(stateTitle), 48);
    }

    const updatedAt = sessionUpdatedAt(session);
    if (updatedAt) {
        const locale = (language || "en").startsWith("sv") ? "sv-SE" : "en-GB";
        return updatedAt.toLocaleString(locale, {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    }

    if (id) {
        return t ? t("agent.chatWithId", { id: id.slice(0, 8) }) : `Chat ${id.slice(0, 8)}`;
    }

    return t ? t("agent.untitledChat") : "Chat";
}

export function sessionEvents(session) {
    const record = sessionRecord(session);
    if (Array.isArray(record?.events)) {
        return record.events;
    }
    if (Array.isArray(record?.session?.events)) {
        return record.session.events;
    }
    return [];
}

export function isUserAuthor(event) {
    const author = String(event?.author || event?.role || "").toLowerCase();
    return author === "user" || author === "human";
}

export function textFromParts(event) {
    return (event?.content?.parts ?? [])
        .map((part) => part.text)
        .filter(Boolean)
        .join("");
}

export function functionCallsFromEvent(event) {
    return (event?.content?.parts ?? []).filter((part) => part.function_call || part.functionCall);
}

export function functionResponsesFromEvent(event) {
    return (event?.content?.parts ?? []).filter((part) => part.function_response || part.functionResponse);
}

export function artifactDeltaFromEvent(event) {
    return event?.actions?.artifact_delta || event?.actions?.artifactDelta || null;
}

export function getToolDisplayLabel(t, name) {
    const key = String(name || "").toLowerCase();
    if (key.includes("sql") || key.includes("bigquery") || key.includes("query")) {
        return t ? t("agent.runningSql") : "Running SQL…";
    }
    if (!name) {
        return t ? t("agent.runningTool") : "Running tool…";
    }
    return t ? t("agent.runningNamedTool", { name }) : `Running ${name}…`;
}

export function toolLabel(name) {
    return getToolDisplayLabel(null, name);
}

export function messagesFromSession(session) {
    const events = sessionEvents(session);
    const messages = [];
    let assistant = null;

    const flushAssistant = () => {
        if (assistant) {
            messages.push(assistant);
            assistant = null;
        }
    };

    events.forEach((event) => {
        if (isUserAuthor(event)) {
            flushAssistant();
            const text = textFromParts(event).trim();
            if (text) {
                messages.push({
                    id: event.id || `user-${messages.length}`,
                    role: "user",
                    text,
                    tools: [],
                    artifacts: [],
                });
            }
            return;
        }

        if (!assistant) {
            assistant = {
                id: event.id || `assistant-${messages.length}`,
                role: "assistant",
                text: "",
                tools: [],
                artifacts: [],
            };
        }

        assistant.text = applyTextEvent(assistant.text, event);
        mergeToolsIntoMessage(assistant, event);
        mergeArtifactsIntoMessage(assistant, event);
    });

    flushAssistant();
    return messages;
}

export function mergeToolsIntoMessage(message, event) {
    const calls = functionCallsFromEvent(event);
    const responses = functionResponsesFromEvent(event);
    if (calls.length === 0 && responses.length === 0) {
        return;
    }

    let tools = message.tools;
    let copied = false;
    const writable = () => {
        if (!copied) {
            tools = tools.map((tool) => ({ ...tool }));
            copied = true;
        }
        return tools;
    };

    calls.forEach((part) => {
        const call = part.function_call || part.functionCall;
        const name = call?.name || "tool";
        const existing = tools.find((tool) => tool.name === name && tool.status === "running");
        if (existing) {
            return;
        }
        writable().push({
            id: `${name}-${tools.length}`,
            name,
            label: toolLabel(name),
            status: "running",
        });
    });

    responses.forEach((part) => {
        const response = part.function_response || part.functionResponse;
        const name = response?.name || "";
        const status = String(response?.response?.status || response?.status || "ok").toLowerCase();
        const running = [...tools].reverse().find((tool) => {
            if (name && tool.name !== name) {
                return false;
            }
            return tool.status === "running";
        });
        if (running) {
            writable();
            const target = tools.find((tool) => tool.id === running.id) || running;
            target.status = status === "error" ? "error" : "done";
        }
    });

    if (copied) {
        message.tools = tools;
    }
}

export function mergeArtifactsIntoMessage(message, event) {
    const delta = artifactDeltaFromEvent(event);
    if (!delta || typeof delta !== "object") {
        return;
    }

    const entries = Object.entries(delta);
    if (entries.length === 0) {
        return;
    }

    let artifacts = message.artifacts;
    let copied = false;
    const writable = () => {
        if (!copied) {
            artifacts = artifacts.map((artifact) => ({ ...artifact }));
            copied = true;
        }
        return artifacts;
    };

    entries.forEach(([name, version]) => {
        const resolvedVersion = resolveArtifactVersion(version);
        const existing = artifacts.find((artifact) => artifact.name === name);
        if (existing) {
            if (existing.version === resolvedVersion) {
                return;
            }
            writable();
            const target = artifacts.find((artifact) => artifact.name === name);
            if (target) {
                target.version = resolvedVersion;
            }
            return;
        }
        writable().push({ name, version: resolvedVersion });
    });

    if (copied) {
        message.artifacts = artifacts;
    }
}

function resolveArtifactVersion(version) {
    if (version == null) {
        return 0;
    }
    if (typeof version === "number" || typeof version === "string") {
        return version;
    }
    if (typeof version === "object") {
        if (version.version != null) {
            return version.version;
        }
        if (version.Version != null) {
            return version.Version;
        }
    }
    return 0;
}

function parseTimestamp(raw) {
    if (raw == null || raw === "") {
        return null;
    }

    if (typeof raw === "number") {
        const ms = raw > 1e12 ? raw : raw * 1000;
        const date = new Date(ms);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof raw === "string") {
        const asNumber = Number(raw);
        if (raw.trim() !== "" && !Number.isNaN(asNumber)) {
            return parseTimestamp(asNumber);
        }
        const date = new Date(raw);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof raw === "object") {
        if (raw.seconds != null) {
            return parseTimestamp(Number(raw.seconds));
        }
        if (raw.Seconds != null) {
            return parseTimestamp(Number(raw.Seconds));
        }
    }

    return null;
}

function truncate(value, max) {
    if (value.length <= max) {
        return value;
    }
    return `${value.slice(0, max - 1)}…`;
}
